import { Box, Collapse, IconButton, Tooltip } from '@mui/material';
import { ExpandMoreOutlined, VisibilityOutlined } from '@mui/icons-material';
import {
    FocusEvent,
    Fragment,
    KeyboardEvent,
    MouseEvent,
    PropsWithChildren,
    ReactElement,
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { PaloThemeProvider, tokens, usePaloThemeGuard } from '@synergy/theme-palo';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { PaloBackButton } from './palo-back-button';
import { PaloDockPlacement, PaloDockPlacementProvider } from './palo-activity-dock';

const c = tokens.color;

/**
 * Answers whether one field holds input that is not saved yet (#2137).
 *
 * A function rather than the data itself, because the answer needs Formik and
 * a baseline of the document as the server has it — neither of which belongs in
 * a presentation library. The admin supplies the lookup; without one every
 * field simply reads as saved, so the components work outside a form too.
 */
export type PaloFieldDirtyLookup = (name: string) => boolean;

const PaloFieldDirtyContext = createContext<PaloFieldDirtyLookup>(() => false);

export const PaloFieldDirtyProvider = PaloFieldDirtyContext.Provider;

export const usePaloFieldDirty = (name?: string | string[]): boolean => {
    const lookup = useContext(PaloFieldDirtyContext);

    if (undefined === name) {
        return false;
    }

    return Array.isArray(name) ? name.some(lookup) : lookup(name);
};

/**
 * Set while a mask shows its own breadcrumbs.
 *
 * The chrome names the area in its top bar, and the mask names it again above
 * the title — read together that is the same word twice, one line apart. The
 * mask wins, because its crumb is the one that can be clicked and stands with
 * the record (#2206).
 */
export interface PaloHeadSlot {
    /**
     * Registers that something on the page names the area itself.
     *
     * It counts rather than switches: a list embedded in a record's shell
     * would otherwise un-register what the shell around it just claimed, and
     * the bar showed both names at once.
     */
    announce: (own: boolean) => void;
    /**
     * Where the mask puts its head row. The chrome offers the space it would
     * otherwise keep empty beside the navigation toggle, so a record mask
     * costs one row instead of two. Null outside the chrome — the row then
     * stays where it is written.
     */
    element: HTMLElement | null;
}

const PaloOwnCrumbContext = createContext<PaloHeadSlot>({ announce: () => undefined, element: null });

export const PaloOwnCrumbProvider = PaloOwnCrumbContext.Provider;

/**
 * The head row slot, for the shells that are not PaloDetailView.
 *
 * The bonus settlement and the import run on their own shell but are records
 * all the same, so they belong in the top bar with the rest.
 */
export const usePaloHeadSlot = (ownCrumb: boolean): PaloHeadSlot => {
    const slot = useContext(PaloOwnCrumbContext);
    const { announce } = slot;

    useEffect(() => {
        if (!ownCrumb) {
            return undefined;
        }

        announce(true);

        return () => announce(false);
    }, [announce, ownCrumb]);

    return slot;
};

/**
 * Puts the name of a list into the top bar.
 *
 * A list keeps its own heading where it is, but the bar used to name the
 * area instead — "Bedarf" over a list everyone else calls
 * "Bedarfserhebungen", the record masks included. One word per place, and
 * one size for it (06.10.2026).
 */
export const PaloPageCrumb = ({ children }: { children: ReactNode }): ReactElement | null => {
    const slot = usePaloHeadSlot(true);

    if (null === slot.element) {
        return null;
    }

    return createPortal(
        <Box sx={{ fontSize: '14px', fontWeight: 640, letterSpacing: '-.015em', color: c.ink }}>{children}</Box>,
        slot.element
    );
};

interface PaloTotalsSlot {
    element: HTMLElement | null;
    register: (used: boolean) => void;
    /** The last figures a producer left behind, for the tabs it is not on. */
    keep: (figures: ReactNode) => void;
}

const PaloTotalsSlotContext = createContext<PaloTotalsSlot>({
    element: null,
    register: () => undefined,
    keep: () => undefined,
});

/**
 * Puts the mask's scroll area back at the top.
 *
 * Form and history share one scroll area, so a tab opened from halfway down the
 * previous one started halfway down itself — on a short tab below the fold
 * (#2212, R-23). The tabs reach the area through this rather than through a
 * prop, because every mask would otherwise have to pass its active tab in just
 * to say "something changed".
 */
const PaloScrollResetContext = createContext<() => void>(() => undefined);

/**
 * The history as a tab of its own, "Aktivitäten (n)", beside the tabs of the
 * mask (#2248). It used to stand at the foot of the scroll area, under the
 * whole form, and grows into the communication record of the document. Every
 * mask gets the tab from here, so none has to add it to its own steps.
 */
interface PaloHistoryTab {
    available: boolean;
    open: boolean;
    count?: number;
    setOpen: (open: boolean) => void;
    setCount: (count?: number) => void;
}

const PaloHistoryTabContext = createContext<PaloHistoryTab>({
    available: false,
    open: false,
    setOpen: () => undefined,
    setCount: () => undefined,
});

/**
 * The history reports how many entries it holds, for the label of its tab.
 * `publish` false leaves the count alone — for a stream that is on screen but
 * not the one the tab stands for.
 */
export const usePaloHistoryCount = (count: number | undefined, publish = true): void => {
    const { setCount } = useContext(PaloHistoryTabContext);

    useEffect(() => {
        if (publish) {
            setCount(count);
        }
    }, [count, publish, setCount]);
};

/** Opens the history tab, e.g. for a shortcut that writes into it. */
export const usePaloOpenHistory = (): (() => void) => {
    const { setOpen } = useContext(PaloHistoryTabContext);

    return useCallback(() => setOpen(true), [setOpen]);
};

/**
 * Places in the header that a form can fill from inside, where it keeps its
 * Formik context: the kind of document in place of the title (Rechnung |
 * Gutschrift, Anfrage | Auktion) and an option beside the main action (an
 * Empfänger senden). Both are values of the form, but they read as part of
 * the header (#2250).
 */
export type PaloDetailSlotName = 'kind' | 'beforeActions';

interface PaloDetailSlots {
    elements: Partial<Record<PaloDetailSlotName, HTMLElement | null>>;
    register: (name: PaloDetailSlotName, used: boolean) => void;
}

const PaloDetailSlotContext = createContext<PaloDetailSlots>({ elements: {}, register: () => undefined });

/** Renders its children into a header slot of the surrounding PaloDetailView. */
export const PaloDetailSlot = ({
    name,
    children,
}: PropsWithChildren<{ name: PaloDetailSlotName }>): ReactElement | null => {
    const { elements, register } = useContext(PaloDetailSlotContext);
    const element = elements[name];

    useEffect(() => {
        register(name, true);

        return () => register(name, false);
    }, [name, register]);

    return element ? createPortal(children, element) : null;
};

const usePaloDetailSlots = () => {
    const [kindElement, setKindElement] = useState<HTMLElement | null>(null);
    const [beforeActionsElement, setBeforeActionsElement] = useState<HTMLElement | null>(null);
    const [used, setUsed] = useState<Partial<Record<PaloDetailSlotName, boolean>>>({});
    const register = useCallback(
        (name: PaloDetailSlotName, isUsed: boolean) => setUsed(current => ({ ...current, [name]: isUsed })),
        []
    );
    const context = useMemo(
        () => ({ elements: { kind: kindElement, beforeActions: beforeActionsElement }, register }),
        [kindElement, beforeActionsElement, register]
    );

    return { context, used, setKindElement, setBeforeActionsElement };
};

/**
 * The totals strip of the surrounding PaloDetailView, to render into.
 *
 * A mask whose totals live inside its form — the order keeps them in its
 * Formik form — cannot hand them over as the `totals` prop without losing the
 * form. Rendered into this element instead, they sit in the strip across the
 * full width of the mask and keep every context they are written in, because a
 * React portal moves the node, not the tree.
 */
export const usePaloTotalsSlot = (): HTMLElement | null => {
    const { element, register } = useContext(PaloTotalsSlotContext);

    useEffect(() => {
        register(true);

        return () => register(false);
    }, [register]);

    return element;
};

/**
 * The figures of the strip, kept for the tabs the form is not on.
 *
 * The totals live in the form of the document tab, so switching to another tab
 * took the strip with them — and FRM-09 has it on every tab. What is left
 * behind here is the reading of the figures, without the fields that change
 * them: those belong to the form, and the form is where they are changed.
 *
 * `key` says when the figures differ; it is what decides whether to publish
 * again, because the node itself is new on every render.
 */
export const usePaloTotalsKeep = (key: string, figures: () => ReactNode): void => {
    const { keep } = useContext(PaloTotalsSlotContext);

    useEffect(() => {
        keep(figures());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, keep]);
};

export interface PaloStep {
    label: ReactNode;
    /** done = past step (green check), now = current (accent), todo = upcoming. */
    state: 'done' | 'now' | 'todo';
    /** When set, the step becomes clickable — for wizard navigation. */
    onClick?: () => void;
    /** Unsaved-changes marker (prototype .dirty: 6px warn dot). */
    dirty?: boolean;
    /**
     * Out of reach for now — a tab that needs a record that does not exist yet.
     * It stays in the row so the shape of the mask does not change on the first
     * save, but it is dimmed and does not answer a click.
     */
    disabled?: boolean;
}

/**
 * Unsaved-changes dot per the prototype's `.dirty`.
 *
 * Accent rather than warn: in amber the marked labels read as errors on a form
 * the user had just filled in correctly (#2184). The state is informational —
 * this field carries input the server has not seen yet — so it takes the colour
 * the rest of the app uses for "active", and warn stays a warning.
 */
const DirtyDot = (): ReactElement => (
    <Box
        component="span"
        title="Ungespeicherte Änderungen"
        sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: c.accent, flexShrink: 0 }}
    />
);

export interface PaloDetailPreview {
    onOpen: () => void;
    /** No PDF yet, e.g. a document never saved. */
    disabled?: boolean;
    /** Why there is none, shown instead of "Vorschau". */
    disabledHint?: ReactNode;
}

const PaloPreviewButton = ({ onOpen, disabled, disabledHint }: PaloDetailPreview): ReactElement => (
    <Tooltip title={disabled && disabledHint ? disabledHint : 'Vorschau'}>
        <span>
            <IconButton size="small" aria-label="Vorschau" onClick={onOpen} disabled={disabled}>
                <VisibilityOutlined sx={{ fontSize: 19 }} />
            </IconButton>
        </span>
    </Tooltip>
);

/**
 * A cell of a position table that marks its own unsaved input, the way a head
 * field does beside its label. A cell has no label, so the dot sits in its
 * upper left corner, away from the figures, which stand on the right; a
 * changed quantity showed nothing anywhere in its row (#2308).
 */
export const PaloDirtyCell = ({ name, children }: PropsWithChildren<{ name: string }>): ReactElement => {
    const isDirty = usePaloFieldDirty(name);

    return (
        <Box sx={{ position: 'relative', minWidth: 0 }}>
            {children}
            {isDirty && (
                <Box sx={{ position: 'absolute', top: -3, left: -3, display: 'flex', pointerEvents: 'none' }}>
                    <DirtyDot />
                </Box>
            )}
        </Box>
    );
};

/**
 * The parts of a document as tabs.
 *
 * It replaces the numbered path the masks used to lead through. A document is
 * a form one fills in, not a sequence one is sent along: the header no longer
 * changes with the step, and every part is one click away (#2207). A part that
 * cannot show anything yet — the chain of a document that does not exist —
 * hands in no handler and stays out of reach without pretending otherwise.
 */
interface PaloTabProps {
    label: ReactNode;
    active: boolean;
    disabled?: boolean;
    dirty?: boolean;
    onClick?: () => void;
}

const PaloTab = ({ label, active, disabled, dirty, onClick }: PaloTabProps): ReactElement => {
    const resetScroll = useContext(PaloScrollResetContext);

    return (
        <Box
            role="tab"
            aria-selected={active}
            aria-disabled={disabled || undefined}
            onClick={
                onClick
                    ? () => {
                          onClick();
                          resetScroll();
                      }
                    : undefined
            }
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 12px 8px',
                fontSize: '12.5px',
                whiteSpace: 'nowrap',
                cursor: onClick ? 'pointer' : 'default',
                color: disabled ? c.ink3 : active ? c.accentHi : c.ink2,
                fontWeight: active ? 620 : 450,
                borderBottom: `2px solid ${active ? c.accent : 'transparent'}`,
                '&:hover': onClick ? { color: c.ink } : undefined,
            }}
        >
            {label}
            {dirty && <DirtyDot />}
        </Box>
    );
};

export const PaloTabs = ({ steps }: { steps: PaloStep[] }): ReactElement => {
    const history = useContext(PaloHistoryTabContext);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                gap: '2px',
                overflowX: 'auto',
                margin: '0 -16px',
                padding: '0 16px',
            }}
        >
            {steps.map((step, index) => {
                const current = 'now' === step.state;
                const active = current && !history.open;
                // While the history is open, its own tab leads back to the form
                // it covers; any other tab still goes where it always went.
                const reachable = !step.disabled && !active && (current || step.onClick);

                return (
                    <PaloTab
                        key={index}
                        label={step.label}
                        active={active}
                        disabled={step.disabled}
                        dirty={step.dirty}
                        onClick={
                            reachable
                                ? () => {
                                      history.setOpen(false);
                                      if (!current) {
                                          step.onClick?.();
                                      }
                                  }
                                : undefined
                        }
                    />
                );
            })}
            {history.available && (
                <PaloTab
                    label={
                        <>
                            Aktivitäten
                            {undefined !== history.count && (
                                <Box component="span" sx={{ color: c.ink3, fontWeight: 450 }}>
                                    {history.count}
                                </Box>
                            )}
                        </>
                    }
                    active={history.open}
                    onClick={history.open ? undefined : () => history.setOpen(true)}
                />
            )}
        </Box>
    );
};

/** Horizontal process stepper per the prototype's `.steps` (Prozessschritte). */
export const PaloStepper = ({ steps }: { steps: PaloStep[] }): ReactElement => {
    const resetScroll = useContext(PaloScrollResetContext);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', overflowX: 'auto', mb: '11px', pb: '2px' }}>
            {steps.map((step, index) => (
                <Fragment key={index}>
                    {index > 0 && <Box sx={{ width: 18, height: '1px', backgroundColor: c.lineHard, flexShrink: 0 }} />}
                    <Box
                        role={step.onClick ? 'button' : undefined}
                        onClick={
                            step.onClick &&
                            (() => {
                                step.onClick?.();
                                resetScroll();
                            })
                        }
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '7px',
                            padding: '5px 13px 5px 11px',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            cursor: step.onClick ? 'pointer' : 'default',
                            color: step.state === 'now' ? c.accentHi : step.state === 'done' ? c.ink2 : c.ink3,
                            fontWeight: step.state === 'now' ? 620 : 400,
                            '&:hover': step.onClick && step.state !== 'now' ? { color: c.ink } : undefined,
                        }}
                    >
                        <Box
                            sx={{
                                width: 17,
                                height: 17,
                                borderRadius: '50%',
                                display: 'grid',
                                placeItems: 'center',
                                fontSize: '10px',
                                lineHeight: 1,
                                fontWeight: 650,
                                flexShrink: 0,
                                backgroundColor:
                                    step.state === 'done' ? c.ok : step.state === 'now' ? c.accent : c.neutralSoft,
                                color: step.state === 'todo' ? c.ink3 : '#fff',
                                boxShadow: step.state === 'now' ? `0 0 0 3px ${c.accentSoft}` : undefined,
                            }}
                        >
                            {step.state === 'done' ? '✓' : index + 1}
                        </Box>
                        {step.label}
                        {step.dirty && <DirtyDot />}
                    </Box>
                </Fragment>
            ))}
        </Box>
    );
};

export interface PaloDetailMeta {
    label: ReactNode;
    value: ReactNode;
}

export interface PaloBreadcrumb {
    label: ReactNode;
    /** Where the crumb leads; the last one has nowhere to go. */
    onClick?: () => void;
}

/**
 * Area and kind of record, the way ERPNext writes it: "Einkauf / Bestellungen".
 *
 * It says where the record belongs, not which way one came — measured in the
 * customer's instance, a record reached from somewhere else still shows its own
 * place (#2206). The record itself is not a crumb; it stands in the title below
 * with its number, so it is written once.
 */
export const PaloBreadcrumbs = ({ items }: { items: PaloBreadcrumb[] }): ReactElement => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', minWidth: 0 }}>
        {items.map((item, index) => (
            <Fragment key={index}>
                {index > 0 && (
                    <Box component="span" sx={{ color: c.ink3, opacity: 0.55 }}>
                        /
                    </Box>
                )}
                <Box
                    component="span"
                    role={item.onClick ? 'button' : undefined}
                    onClick={item.onClick}
                    title={'string' === typeof item.label ? item.label : undefined}
                    sx={{
                        color: c.ink3,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: item.onClick ? 'pointer' : 'default',
                        '&:hover': item.onClick ? { color: c.accentHi } : undefined,
                    }}
                >
                    {item.label}
                </Box>
            </Fragment>
        ))}
    </Box>
);

export interface PaloDetailViewProps {
    /**
     * Where the record sits — area, kind, number. It replaces the back arrow,
     * which said only that there was a way back, not where to (#2207).
     */
    breadcrumbs?: PaloBreadcrumb[];
    /** Back affordance; omit to hide the back button. */
    onBack?: () => void;
    /** Monospace document number shown above the title. */
    number?: ReactNode;
    title: ReactNode;
    /** Status slot next to the title, e.g. a PaloStatusPill. */
    status?: ReactNode;
    /** Unsaved-changes marker next to the title (prototype .dirty). */
    dirty?: boolean;
    /** Compact meta row under the title. */
    meta?: PaloDetailMeta[];
    /**
     * Secondary buttons in the header, left of the menu. Everything that is
     * not the next logical step belongs in `menu` instead.
     */
    actions?: ReactNode;
    /**
     * The one filled button, farthest right: the next logical step. One mask,
     * one such button — the rest of what can be done sits in the menu beside
     * it, so the eye always finds the same thing in the same place (#2206).
     */
    primaryAction?: ReactNode;
    /** The overflow menu (three dots) beside the primary action. */
    menu?: ReactNode;
    /**
     * The document's PDF, behind an eye left of the primary action: in the
     * same place on every document, the menu keeps its entry as well (#2316).
     */
    preview?: PaloDetailPreview;
    /** Slot before the menu, e.g. arrows through the records of a list. */
    pager?: ReactNode;
    /**
     * Notice about the document itself (Vermerk), shown under the title and
     * above the steps. It belongs in the header, not in the history at the foot
     * of the mask, where it was only found by scrolling (#2184).
     */
    alert?: ReactNode;
    /** Process steps (Prozessschritte) shown under the title, above the tabs. */
    steps?: PaloStep[];
    /** Optional row under the header (tabs, stepper). */
    tabs?: ReactNode;
    /**
     * The document's history — a PaloHistory. It gets a tab of its own,
     * "Aktivitäten", after the mask's tabs (#2248). At the foot of the form it
     * was reached by scrolling past everything else, and it is about to become
     * the record of every mail, call and note on the document.
     */
    history?: ReactNode;
    /**
     * Row under the tabs that stays put while the form scrolls. The invoice
     * and the order keep their totals here: what such a document is about is
     * the sum, and it was the one thing that scrolled away. It sits at the top
     * with everything else that steers the mask (#2206).
     */
    totals?: ReactNode;
    /** Docked panel next to the body, e.g. a PaloSideRail. */
    dock?: ReactNode;
    /**
     * Which edge the dock sits against (default right). Purchasing and
     * administration read the notes while they work the document, and asked for
     * them back at the right edge where they are visible without scrolling
     * (#2184). Set 'bottom' for a mask that would rather have the width.
     */
    dockPlacement?: PaloDockPlacement;
    /**
     * Whether to render the body under the PALO theme (default true). Set false
     * when embedding legacy content that expects the app's ambient theme.
     */
    themeContent?: boolean;
    /**
     * Gives the content the whole height of the body instead of letting it
     * scroll. For a page that is one large table, which brings its own
     * scrolling: in the shared scroll area a table asking for 100 % gets
     * nothing and draws no row (#2268, #2269). Such a page has no history at
     * the foot to scroll to.
     */
    fillBody?: boolean;
}

/**
 * PALO detail chrome per the prototype: a fixed header (back, document number,
 * title + status, meta, actions), an optional tab/stepper row and a scrollable
 * body on the canvas. The body hosts PaloFormSection / PaloFormGrid content.
 */
export const PaloDetailView = ({
    breadcrumbs,
    onBack,
    number,
    title,
    status,
    dirty,
    meta,
    actions,
    primaryAction,
    menu,
    preview,
    pager,
    alert,
    steps,
    tabs,
    history,
    totals,
    dock,
    dockPlacement = 'right',
    themeContent = true,
    fillBody = false,
    children,
}: PropsWithChildren<PaloDetailViewProps>): ReactElement => {
    const bodyElement = useRef<HTMLElement | null>(null);
    const resetScroll = useCallback(() => bodyElement.current?.scrollTo({ top: 0 }), []);
    const [totalsElement, setTotalsElement] = useState<HTMLElement | null>(null);
    const [totalsSlotUsed, setTotalsSlotUsed] = useState(false);
    // What the form left behind, shown on the tabs the form is not on.
    const [keptTotals, setKeptTotals] = useState<ReactNode>(null);
    const register = useCallback((used: boolean) => setTotalsSlotUsed(used), []);
    const keep = useCallback((figures: ReactNode) => setKeptTotals(figures), []);
    const hasCrumbs = Boolean(breadcrumbs && breadcrumbs.length > 0);
    const slots = usePaloDetailSlots();
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyCount, setHistoryCount] = useState<number | undefined>(undefined);
    const { pathname } = useLocation();
    // A document always opens on its first tab; paging to the next record with
    // the history open would otherwise land there (#2248).
    useEffect(() => setHistoryOpen(false), [pathname]);
    const historyTab = useMemo(
        () => ({
            available: Boolean(history),
            open: historyOpen && Boolean(history),
            count: historyCount,
            setOpen: setHistoryOpen,
            setCount: setHistoryCount,
        }),
        [history, historyOpen, historyCount]
    );
    const headSlot = usePaloHeadSlot(hasCrumbs);
    const totalsSlot = useMemo(() => ({ element: totalsElement, register, keep }), [totalsElement, register, keep]);
    const form = (
        <Box
            sx={{
                padding: '14px 16px',
                ...(fillBody && { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }),
            }}
        >
            {children}
        </Box>
    );
    const themedForm = themeContent ? <PaloThemeProvider>{form}</PaloThemeProvider> : form;

    /**
     * The form or the history, one at a time. Both stay mounted: the form keeps
     * what was typed into it, and the history keeps counting for its tab.
     */
    const themedBody = (
        <Box
            ref={bodyElement}
            sx={{
                flex: 1,
                minHeight: 0,
                overflowY: fillBody ? 'hidden' : 'auto',
                ...(fillBody && { display: 'flex', flexDirection: 'column' }),
            }}
        >
            <Box
                sx={{
                    display: historyTab.open ? 'none' : fillBody ? 'flex' : undefined,
                    ...(fillBody && { flex: 1, minHeight: 0, flexDirection: 'column' }),
                }}
            >
                {themedForm}
            </Box>
            {history && <Box sx={{ display: historyTab.open ? undefined : 'none' }}>{history}</Box>}
        </Box>
    );

    /**
     * Everything the document adds up to, under the tabs and above the form.
     *
     * It used to sit at the foot of the mask. Whatever steers a document now
     * stands at the top and stays there, so the sum is readable from any
     * scroll position without a second strip at the bottom (#2206).
     */
    const totalsBar = (
        <Box
            ref={setTotalsElement}
            sx={{
                display: totals || totalsSlotUsed || keptTotals ? 'block' : 'none',
                // Past the padding of the header, so the row spans the mask.
                margin: '0 -16px',
                borderTop: `1px solid ${c.line}`,
                padding: '7px 16px',
            }}
        >
            {totals}
            {/* The form is on another tab: its figures stay, the fields that
                change them do not (FRM-09). */}
            {!totalsSlotUsed && keptTotals}
        </Box>
    );

    const atRight = dockPlacement === 'right' && Boolean(dock);

    const headerActions =
        actions || primaryAction || menu || pager || slots.used.beforeActions ? (
            <Box
                sx={{
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                }}
            >
                {/* The step that is due always ends the row, whether it is
                    saving or what comes after it — otherwise the filled button
                    jumps across the menu the moment something is typed. Only
                    one of `actions` and `primaryAction` carries it, because a
                    flow action that is not the step renders nothing. */}
                {pager}
                {menu}
                <Box
                    component="span"
                    ref={slots.setBeforeActionsElement}
                    sx={{ display: slots.used.beforeActions ? 'inline-flex' : 'none', alignItems: 'center' }}
                />
                {preview && <PaloPreviewButton {...preview} />}
                {actions}
                {primaryAction}
            </Box>
        ) : null;

    const inHeadSlot = null !== headSlot.element;

    /**
     * Where one is, what one is looking at, and what can be done here — one
     * row, as in ERPNext. The chrome takes it into its top bar when it offers
     * the space, which saves the mask a whole row (06.10.2026).
     */
    // Read back off the screen rather than taken from the prop: a record names
    // itself with a message more often than with a plain string.
    const titleRef = useRef<HTMLSpanElement | null>(null);

    useLayoutEffect(() => {
        titleRef.current?.setAttribute('title', titleRef.current.textContent ?? '');
    });

    const headRow = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                minHeight: 30,
                // In the top bar the row fills the width and keeps to one line;
                // written inline it keeps the margin that sets it off from the
                // notices below.
                ...(inHeadSlot ? { width: '100%', minWidth: 0, flexWrap: 'nowrap' } : { mb: '9px', flexWrap: 'wrap' }),
            }}
        >
            {breadcrumbs && breadcrumbs.length > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                    <PaloBreadcrumbs items={breadcrumbs} />
                    <Box component="span" sx={{ color: c.ink3, opacity: 0.55, fontSize: '14px' }}>
                        /
                    </Box>
                </Box>
            ) : (
                onBack && <PaloBackButton onClick={onBack} />
            )}
            <Box
                sx={{
                    // One size for the whole row: crumb, number and title
                    // at 14px. Weight and colour tell them apart, not size —
                    // a title at 17 over a crumb at 11.5 read as three
                    // unrelated things (06.10.2026).
                    fontSize: '14px',
                    fontWeight: 640,
                    letterSpacing: '-.015em',
                    color: c.ink,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '9px',
                    minWidth: 0,
                    // In the bar the row has one line and no more.
                    flexWrap: inHeadSlot ? 'nowrap' : 'wrap',
                    ...(inHeadSlot && { overflow: 'hidden' }),
                }}
            >
                {number !== undefined && (
                    <Box
                        component="span"
                        sx={{
                            // The number is how users name the document to
                            // each other; at 11.5px grey it read as a
                            // caption (#2184), and in grey monospace still as
                            // the smaller half of the title (#2318).
                            fontVariantNumeric: 'tabular-nums',
                            color: c.ink,
                        }}
                    >
                        {number}
                    </Box>
                )}
                <Box
                    component="span"
                    // Same as the list: what the bar cuts off, the hover says,
                    // read back off the screen because a title is rarely a
                    // plain string.
                    ref={titleRef}
                    sx={{
                        // A form that offers the kind of document as a choice
                        // shows that choice instead; the title is its fixed label.
                        display: slots.used.kind ? 'none' : undefined,
                        ...(inHeadSlot && {
                            minWidth: 0,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }),
                    }}
                >
                    {title}
                </Box>
                {/* Outside the title, which the bar cuts off with an ellipsis:
                    a control there would be clipped and its labels would run
                    into the title's tooltip. */}
                <Box
                    component="span"
                    ref={slots.setKindElement}
                    sx={{ display: slots.used.kind ? 'inline-flex' : 'none', flexShrink: 0 }}
                />
                {status}
                {dirty && <DirtyDot />}
            </Box>
            {headerActions}
        </Box>
    );

    const portalledHead = inHeadSlot ? createPortal(headRow, headSlot.element as HTMLElement) : null;
    const inlineHead = inHeadSlot ? null : headRow;

    return (
        <>
            {portalledHead}
            <PaloScrollResetContext.Provider value={resetScroll}>
                <PaloDetailSlotContext.Provider value={slots.context}>
                    <PaloHistoryTabContext.Provider value={historyTab}>
                        <PaloTotalsSlotContext.Provider value={totalsSlot}>
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: c.canvas,
                                }}
                            >
                                <PaloThemeProvider>
                                    <Box
                                        sx={{
                                            backgroundColor: c.surface,
                                            borderBottom: `1px solid ${c.line}`,
                                            padding: '8px 16px 0',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {inlineHead}

                                        {meta && meta.length > 0 && (
                                            <Box
                                                sx={{
                                                    fontSize: '11.5px',
                                                    color: c.ink3,
                                                    mt: '-4px',
                                                    mb: '9px',
                                                    display: 'flex',
                                                    gap: '12px',
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                {meta.map((entry, index) => (
                                                    <Box component="span" key={index}>
                                                        {entry.label}{' '}
                                                        <Box component="b" sx={{ color: c.ink2, fontWeight: 550 }}>
                                                            {entry.value}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                        {/* The slot is always filled with an element, which may
                                decide it has nothing to show — without :empty its
                                margin would hold 11px open above the tabs. */}
                                        {alert && (
                                            <Box sx={{ mb: '11px', '&:empty': { display: 'none' } }}>{alert}</Box>
                                        )}
                                        {steps && steps.length > 0 && <PaloStepper steps={steps} />}
                                        {tabs}
                                        {totalsBar}
                                    </Box>
                                </PaloThemeProvider>

                                {atRight ? (
                                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
                                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                            {themedBody}
                                        </Box>
                                        <PaloDockPlacementProvider value="right">{dock}</PaloDockPlacementProvider>
                                    </Box>
                                ) : (
                                    <>
                                        {themedBody}
                                        {dock}
                                    </>
                                )}
                            </Box>
                        </PaloTotalsSlotContext.Provider>
                    </PaloHistoryTabContext.Provider>
                </PaloDetailSlotContext.Provider>
            </PaloScrollResetContext.Provider>
        </>
    );
};

export interface PaloFormSectionProps {
    title: ReactNode;
    /** Right-aligned slot before the collapse toggle. */
    action?: ReactNode;
    defaultOpen?: boolean;
    collapsible?: boolean;
}

/** Collapsible, uppercase-titled form section per the prototype's `.fsec`. */
export const PaloFormSection = ({
    title,
    action,
    defaultOpen = true,
    collapsible = true,
    children,
}: PropsWithChildren<PaloFormSectionProps>): ReactElement => {
    const [open, setOpen] = useState(defaultOpen);
    usePaloThemeGuard('PaloFormSection');

    return (
        <Box sx={{ mb: '20px' }}>
            <Box
                sx={{
                    fontSize: '10.5px',
                    fontWeight: 640,
                    letterSpacing: '.08em',
                    textTransform: 'uppercase',
                    color: c.ink3,
                    pb: '5px',
                    borderBottom: `1px solid ${c.line}`,
                    mb: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}
            >
                {title}
                <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {action}
                    {collapsible && (
                        <Box
                            role="button"
                            aria-label={open ? 'Einklappen' : 'Ausklappen'}
                            onClick={() => setOpen(value => !value)}
                            sx={{
                                cursor: 'pointer',
                                color: c.ink3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px',
                                fontSize: '11px',
                                fontWeight: 400,
                                letterSpacing: 'normal',
                                textTransform: 'none',
                                '& svg': {
                                    fontSize: 16,
                                    transition: 'transform .15s',
                                    transform: open ? 'none' : 'rotate(-90deg)',
                                },
                            }}
                        >
                            {/* The prototype labels the toggle instead of showing a bare chevron. */}
                            {open ? 'Einklappen' : 'Ausklappen'}
                            <ExpandMoreOutlined />
                        </Box>
                    )}
                </Box>
            </Box>
            <Collapse in={open} unmountOnExit={false}>
                {children}
            </Collapse>
        </Box>
    );
};

export interface PaloFormGridProps {
    /** Maximum columns on wide viewports (default 3). Steps down to 2 then 1. */
    columns?: 2 | 3 | 4;
    /**
     * Places fields by name instead of by order, one string per row in the
     * notation of `grid-template-areas` (a name repeated is a wider field, `.`
     * an empty cell). Each field names its cell with `area`. A head assembled
     * from several sections can then mix their fields within one row while the
     * sections keep their own logic (#2250). Applies from `lg` upwards; below,
     * the fields flow in document order.
     */
    areas?: string[];
    /**
     * The same fields for three columns. A grid of four steps down to three
     * while it is narrower than PALO_FOUR_COLUMNS_FROM: beside the menu and
     * the attachments a document head is about 900 pixels wide, and four
     * columns left a search field with its two buttons a single pixel for
     * the name (#2262).
     */
    narrowAreas?: string[];
    /**
     * Label above the control, or beside it. Beside halves the height of a
     * row, which is what lets a document head show its positions without
     * scrolling (#2250).
     */
    labels?: PaloFieldLabelPlacement;
}

export type PaloFieldLabelPlacement = 'top' | 'side';

const PaloFieldLabelContext = createContext<PaloFieldLabelPlacement>('top');

const gridColumns = (columns: 2 | 3 | 4) =>
    ({
        2: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        3: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
        4: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
    }[columns]);

/**
 * The width from which a grid of four columns has them: four times a side
 * label, its gap and a field of about 180 pixels, which is what a search field
 * with its two buttons needs to show a name.
 */
export const PALO_FOUR_COLUMNS_FROM = 1250;

const templateAreas = (rows?: string[]) => rows?.map(row => `"${row}"`).join(' ');

const FOCUSABLE = 'input, select, textarea, button, a[href], [tabindex]';

/**
 * Marks a control inside a field that Tab stops at in a form grid. Only the
 * calendar button of a date carries it; the other symbols beside a field —
 * clear, contact card — are for the mouse (#2250).
 */
const PALO_TAB_STOP = 'data-palo-tab-stop';

const isField = (element: HTMLElement): boolean =>
    ['INPUT', 'SELECT', 'TEXTAREA'].includes(element.tagName) || element.hasAttribute(PALO_TAB_STOP);

const cellOf = (grid: HTMLElement, element: HTMLElement): HTMLElement => {
    let cell = element;

    while (cell.parentElement && cell.parentElement !== grid) {
        cell = cell.parentElement;
    }

    return cell;
};

/** Everything Tab could reach inside the grid, in document order. */
const reachableIn = (grid: HTMLElement): HTMLElement[] =>
    Array.from(grid.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        element =>
            element.tabIndex >= 0 &&
            !(element as HTMLInputElement).disabled &&
            'hidden' !== (element as HTMLInputElement).type &&
            null !== element.offsetParent
    );

/** The fields Tab stops at inside the grid, in the order the eye reads them. */
const focusOrder = (grid: HTMLElement): HTMLElement[] => {
    const fields = reachableIn(grid).filter(isField);
    const place = new Map<HTMLElement, DOMRect>();

    fields.forEach(element => {
        const cell = cellOf(grid, element);

        if (!place.has(cell)) {
            place.set(cell, cell.getBoundingClientRect());
        }
    });

    // Row by row, left to right; inside one cell the document order stands,
    // so a date keeps its calendar button right behind it. Sort is stable.
    return fields
        .map((element, index) => ({ element, index, at: place.get(cellOf(grid, element)) as DOMRect }))
        .sort((a, b) => Math.round(a.at.top) - Math.round(b.at.top) || a.at.left - b.at.left || a.index - b.index)
        .map(entry => entry.element);
};

/**
 * Tab follows the grid as it is drawn.
 *
 * Areas place a field anywhere, but Tab walks the document, and the heads are
 * written in parts — recipient, order, delivery, invoice — that each fill
 * cells across several rows. Tab jumped from the cost centre down to the order
 * reference and back up to the supplier (#2250, R-214). No tabIndex: a
 * positive one would reorder the rest of the page as well.
 */
const followDrawnOrder = (event: KeyboardEvent<HTMLElement>): void => {
    if ('Tab' !== event.key || event.altKey || event.ctrlKey || event.metaKey) {
        return;
    }

    const grid = event.currentTarget;
    const active = document.activeElement as HTMLElement | null;

    if (!active || !grid.contains(active)) {
        return;
    }

    const drawn = focusOrder(grid);
    let next: HTMLElement | undefined;

    if (drawn.includes(active)) {
        next = drawn[drawn.indexOf(active) + (event.shiftKey ? -1 : 1)];
    } else {
        // A symbol beside a field, reached by the mouse: go on from its field.
        const cell = cellOf(grid, active);
        const own = drawn.filter(element => cellOf(grid, element) === cell);

        if (0 === own.length) {
            return;
        }

        const before = own.filter(
            element => element.compareDocumentPosition(active) & Node.DOCUMENT_POSITION_FOLLOWING
        );

        next = event.shiftKey
            ? before[before.length - 1] ?? drawn[drawn.indexOf(own[0]) - 1]
            : drawn[drawn.indexOf(own[own.length - 1]) + 1];
    }

    event.preventDefault();

    if (next) {
        next.focus();

        return;
    }

    // Leaving the grid: hand over to the browser from its first or last
    // reachable element, so it goes on to what lies before or after the grid
    // and not to a symbol inside it.
    const reachable = reachableIn(grid);
    const edge = event.shiftKey ? reachable[0] : reachable[reachable.length - 1];
    const sequence = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        element =>
            element.tabIndex >= 0 &&
            !(element as HTMLInputElement).disabled &&
            null !== element.offsetParent &&
            !grid.contains(element)
    );
    const beyond = event.shiftKey
        ? sequence.filter(element => element.compareDocumentPosition(edge) & Node.DOCUMENT_POSITION_FOLLOWING).pop()
        : sequence.find(element => edge.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING);

    beyond?.focus();
};

/**
 * Coming into the grid by Tab, the focus lands on the first field as drawn,
 * and by Shift+Tab on the last — not on whatever the document has first or
 * last. A click goes where it was meant to.
 */
const enterDrawnOrder =
    (byTab: { current: boolean }) =>
    (event: FocusEvent<HTMLElement>): void => {
        const grid = event.currentTarget;
        const from = event.relatedTarget as Node | null;

        if (!byTab.current || !from || grid.contains(from)) {
            return;
        }

        const drawn = focusOrder(grid);
        const fromBelow = !!(grid.compareDocumentPosition(from) & Node.DOCUMENT_POSITION_FOLLOWING);
        const target = fromBelow ? drawn[drawn.length - 1] : drawn[0];

        if (target && target !== event.target) {
            target.focus();
        }
    };

/** Whether the focus is moving because of the Tab key, for enterDrawnOrder. */
const useTabbing = (enabled: boolean): { current: boolean } => {
    const tabbing = useRef(false);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        const note = (event: globalThis.KeyboardEvent): void => {
            tabbing.current = 'Tab' === event.key;
        };
        const forget = (): void => {
            tabbing.current = false;
        };

        document.addEventListener('keydown', note, true);
        document.addEventListener('mousedown', forget, true);

        return () => {
            document.removeEventListener('keydown', note, true);
            document.removeEventListener('mousedown', forget, true);
        };
    }, [enabled]);

    return tabbing;
};

/** Dense, responsive form grid per the prototype's `.fgrid` (3 → 2 → 1 columns). */
export const PaloFormGrid = ({
    columns = 3,
    areas,
    narrowAreas,
    labels = 'top',
    children,
}: PropsWithChildren<PaloFormGridProps>): ReactElement => {
    // Four columns are a question of the room the grid has, not of the window:
    // the same window holds a wide head on a list and a narrow one beside the
    // attachments. Without a layout for three, a grid with areas keeps four.
    const steps = 4 === columns && (!areas || narrowAreas);
    const tabbing = useTabbing(!!areas);

    return (
        <PaloFieldLabelContext.Provider value={labels}>
            <Box sx={{ containerType: steps ? 'inline-size' : undefined, mb: '6px' }}>
                <Box
                    onKeyDown={areas ? followDrawnOrder : undefined}
                    onFocus={areas ? enterDrawnOrder(tabbing) : undefined}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: gridColumns(steps ? 3 : columns),
                        gridTemplateAreas: areas ? { lg: templateAreas(steps ? narrowAreas : areas) } : undefined,
                        // Side labels make a head of single-line rows; 4 px between them
                        // keeps it as dense as the templates of #2250/#2251.
                        gap: labels === 'side' ? '4px 18px' : '12px 18px',
                        ...(steps
                            ? {
                                  [`@container (min-width: ${PALO_FOUR_COLUMNS_FROM}px)`]: {
                                      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                                      gridTemplateAreas: templateAreas(areas),
                                  },
                              }
                            : {}),
                    }}
                >
                    {children}
                </Box>
            </Box>
        </PaloFieldLabelContext.Provider>
    );
};

/**
 * A value too long for its field is cut with an ellipsis; hovering then says it
 * in full (#2250). Read off the input itself, because the field does not know
 * what control it holds.
 */
const revealClippedValue = (event: MouseEvent<HTMLElement>) => {
    const input = event.currentTarget.querySelector('input');

    if (!input) {
        return;
    }

    if (input.scrollWidth > input.clientWidth) {
        input.title = input.value;
    } else {
        input.removeAttribute('title');
    }
};

/** Width of a label beside its control; equal in every cell so the controls line up. */
const SIDE_LABEL_WIDTH = 116;

export interface PaloFieldProps {
    label: ReactNode;
    hint?: ReactNode;
    /** Field-level error; replaces the hint and turns it red. */
    error?: ReactNode;
    /** Grid columns to span (for wide fields). */
    span?: number;
    /** The cell this field takes in a grid with `areas`. */
    area?: string;
    /**
     * The Formik path this field edits. Set it and the field marks itself while
     * it holds unsaved input — the customer's wish from the 2026-07-21 meeting,
     * after ERPNext. Left out, the field behaves as before.
     *
     * A list for a field that edits more than one value, e.g. a delivery date
     * that carries a kind alongside the date itself.
     */
    name?: string | string[];
    /** Overrides the lookup, for fields whose value does not live in Formik. */
    dirty?: boolean;
    /**
     * A mark beside the label, for something about the value the control
     * itself cannot say — a partner locked on the master record since the
     * document was written (GF 12.10.2026, #2230).
     */
    badge?: ReactNode;
}

/**
 * Labelled form field: label on top, control/value below, field-level hint or
 * error underneath (prototype `.f`). The child is the value or an input, so the
 * same field works for read and edit modes.
 */
export const PaloField = ({
    label,
    hint,
    error,
    span,
    area,
    name,
    dirty,
    badge,
    children,
}: PropsWithChildren<PaloFieldProps>): ReactElement => {
    usePaloThemeGuard('PaloField');

    const placement = useContext(PaloFieldLabelContext);
    const lookedUp = usePaloFieldDirty(name);
    const isDirty = dirty ?? lookedUp;
    const side = placement === 'side';
    // Beside its label a field says what is wrong in red and on hover: a line
    // under it made its row twice as high and moved the column beside it out
    // of line (#2262). Above-label fields keep the line, it moves nothing.
    const sideError = side && !!error;

    return (
        <Box
            sx={{
                display: side ? 'grid' : 'flex',
                flexDirection: 'column',
                gridTemplateColumns: side ? `${SIDE_LABEL_WIDTH}px minmax(0, 1fr)` : undefined,
                alignItems: side ? 'center' : undefined,
                gap: side ? '0 10px' : '3px',
                minWidth: 0,
                // The cell is as tall as its row; a control that wants the whole
                // of it (a drop zone, a remark) can then have it, and one that
                // does not keeps its own height.
                height: '100%',
                gridColumn: span ? `span ${span}` : undefined,
                gridArea: area ? { lg: area } : undefined,
                // Beside its label a control keeps an empty one of its own, and
                // MUI hides the placeholder under a label that has not moved
                // up. A hint that belongs to an empty field goes there instead
                // of under it, where it doubled the height of its row (#2262).
                ...(side
                    ? {
                          '& label[data-shrink=false] + .MuiInputBase-formControl .MuiInputBase-input::placeholder': {
                              opacity: '1 !important',
                              color: c.ink3,
                          },
                      }
                    : {}),
            }}
        >
            <Box
                component="label"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    minWidth: 0,
                    fontSize: '11px',
                    fontWeight: 520,
                    // The label carries the state, not the input: a coloured
                    // border around the control would fight the focus ring and
                    // the error state for the same edge.
                    color: sideError ? c.crit : isDirty ? c.accentHi : c.ink3,
                    // Beside the control a label has a fixed width; a long one
                    // wraps to a second line rather than pushing the control.
                    lineHeight: side ? 1.25 : undefined,
                }}
            >
                {label}
                {isDirty && <DirtyDot />}
                {/* Beside the control the label has 116px, and a pill there
                    broke "Lieferant *" onto two lines; it goes behind the
                    control instead. */}
                {!side && badge}
            </Box>
            {/* Grows into whatever the row leaves over. A control that asks for
                the height takes it, one that does not keeps its own. */}
            <Tooltip title={sideError ? error : ''} placement="bottom-start">
                <Box
                    onMouseOver={revealClippedValue}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        // Red also where the control has no error state of its
                        // own, or is switched off until another field is set.
                        ...(sideError
                            ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: `${c.crit} !important` } }
                            : {}),
                    }}
                >
                    {side && badge ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
                            {badge}
                        </Box>
                    ) : (
                        children
                    )}
                </Box>
            </Tooltip>
            {(error || hint) && !sideError && (
                <Box
                    sx={{
                        fontSize: '10.5px',
                        color: error ? c.crit : c.ink3,
                        gridColumn: side ? 2 : undefined,
                    }}
                >
                    {error || hint}
                </Box>
            )}
        </Box>
    );
};

export interface PaloReadValueProps {
    mono?: boolean;
    strong?: boolean;
}

/** Read-only value styled per the prototype's `.f .val`. */
export const PaloReadValue = ({ mono, strong, children }: PropsWithChildren<PaloReadValueProps>): ReactElement => (
    <Box
        sx={{
            fontSize: '13px',
            padding: '5px 0',
            minHeight: 26,
            color: c.ink,
            fontVariantNumeric: mono ? 'tabular-nums' : undefined,
            fontWeight: strong ? 620 : undefined,
        }}
    >
        {children}
    </Box>
);
